<template>
  <div class="page-container">
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>

    <header class="page-header">
      <div class="breadcrumb">
        <router-link to="/" class="breadcrumb-link">{{ $i18n.t('home.label') === 'DEV TOOLS' ? 'Home' : '首页' }}</router-link>
        <span class="breadcrumb-sep">/</span>
        <span class="breadcrumb-current">{{ $i18n.t('pages.qrcode.title') }}</span>
      </div>
      <div class="header-content">
        <div class="header-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="3" y="3" width="7" height="7"/>
            <rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/>
            <rect x="14" y="14" width="3" height="3"/>
            <rect x="18" y="14" width="3" height="3"/>
            <rect x="14" y="18" width="3" height="3"/>
            <rect x="18" y="18" width="3" height="3"/>
          </svg>
        </div>
        <h1 class="page-title">{{ $i18n.t('pages.qrcode.title') }}</h1>
        <p class="page-subtitle">{{ $i18n.t('pages.qrcode.subtitle') }}</p>
      </div>
    </header>

    <main class="page-content">
      <div class="converter-card">
        <div class="input-section">
          <label class="input-label">{{ $i18n.locale === 'zh' ? '输入链接' : 'Input URL' }}</label>
          <div class="input-wrapper">
            <input
              type="text"
              v-model="inputUrl"
              :placeholder="$i18n.t('pages.qrcode.inputPlaceholder')"
              class="url-input"
              @keyup.enter="generateQRCode"
            />
            <button @click="generateQRCode" class="generate-btn" :disabled="!inputUrl.trim()">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
              {{ $i18n.t('pages.qrcode.button') }}
            </button>
          </div>
        </div>

        <div v-if="qrCodeData" class="output-section">
          <label class="output-label">{{ $i18n.locale === 'zh' ? '生成的二维码' : 'Generated QR Code' }}</label>
          <div class="qrcode-wrapper">
            <img :src="qrCodeData" alt="QR Code" class="qrcode-image" />
          </div>
          <p class="qrcode-url">{{ inputUrl }}</p>
          <button @click="downloadQRCode" class="download-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {{ $i18n.t('pages.qrcode.download') }}
          </button>
        </div>

        <div v-else class="placeholder">
          <div class="placeholder-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
              <rect x="3" y="3" width="7" height="7"/>
              <rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/>
              <rect x="14" y="14" width="3" height="3"/>
              <rect x="18" y="14" width="3" height="3"/>
              <rect x="14" y="18" width="3" height="3"/>
              <rect x="18" y="18" width="3" height="3"/>
            </svg>
          </div>
          <p>{{ $i18n.t('pages.qrcode.placeholder') }}</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import QRCode from 'qrcode'

export default {
  name: 'QRCodeGeneratorPage',
  inject: ['i18n'],
  data() {
    return {
      inputUrl: '',
      qrCodeData: null
    }
  },
  methods: {
    async generateQRCode() {
      if (!this.inputUrl.trim()) {
        return
      }

      try {
        const canvas = await QRCode.toCanvas(this.inputUrl, {
          width: 280,
          margin: 2,
          color: {
            dark: '#f0f0f0',
            light: '#1a1a24'
          }
        })
        this.qrCodeData = canvas.toDataURL('image/png')
      } catch (error) {
        console.error('生成二维码失败:', error)
      }
    },
    downloadQRCode() {
      if (!this.qrCodeData) {
        return
      }

      const link = document.createElement('a')
      link.href = this.qrCodeData
      link.download = `qrcode-${Date.now()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  padding: 40px 24px 60px;
  position: relative;
  overflow: hidden;
}

.bg-grid {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    linear-gradient(rgba(232, 255, 0, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(232, 255, 0, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(232, 255, 0, 0.1) 0%, transparent 70%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  filter: blur(100px);
  pointer-events: none;
  z-index: 0;
}

.page-header {
  max-width: 600px;
  margin: 0 auto 50px;
  position: relative;
  z-index: 1;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 30px;
  font-family: var(--font-mono);
  font-size: 12px;
}

.breadcrumb-link {
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.3s;
}

.breadcrumb-link:hover {
  color: var(--accent-primary);
}

.breadcrumb-sep {
  color: var(--border-color);
}

.breadcrumb-current {
  color: var(--accent-primary);
}

.header-content {
  text-align: center;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  color: var(--accent-primary);
  margin-bottom: 20px;
}

.page-title {
  font-family: var(--font-display);
  font-size: 48px;
  letter-spacing: 2px;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  font-weight: 300;
}

.page-content {
  max-width: 600px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.converter-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 40px;
}

.input-section {
  margin-bottom: 32px;
}

.input-label,
.output-label {
  display: block;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 12px;
}

.input-wrapper {
  display: flex;
  gap: 12px;
}

.url-input {
  flex: 1;
  padding: 16px 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  color: var(--text-primary);
  font-family: var(--font-mono);
  font-size: 15px;
  transition: all 0.3s ease;
}

.url-input::placeholder {
  color: var(--text-secondary);
}

.url-input:focus {
  outline: none;
  border-color: var(--accent-primary);
  box-shadow: 0 0 0 3px rgba(232, 255, 0, 0.1);
}

.generate-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 24px;
  background: var(--accent-primary);
  color: var(--bg-primary);
  border: none;
  border-radius: 12px;
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.generate-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(232, 255, 0, 0.3);
}

.generate-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.output-section {
  text-align: center;
}

.qrcode-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--bg-secondary);
  border-radius: 16px;
  margin-bottom: 16px;
}

.qrcode-image {
  width: 200px;
  height: 200px;
  display: block;
}

.qrcode-url {
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--text-secondary);
  margin-bottom: 24px;
  word-break: break-all;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: transparent;
  color: var(--accent-primary);
  border: 1px solid var(--accent-primary);
  border-radius: 10px;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.download-btn:hover {
  background: var(--accent-primary);
  color: var(--bg-primary);
}

.placeholder {
  text-align: center;
  padding: 60px 20px;
  background: var(--bg-secondary);
  border-radius: 16px;
  border: 1px dashed var(--border-color);
}

.placeholder-icon {
  color: var(--border-color);
  margin-bottom: 16px;
}

.placeholder p {
  color: var(--text-secondary);
  font-size: 15px;
}

@media (max-width: 640px) {
  .page-container {
    padding: 24px 16px 40px;
  }

  .page-title {
    font-size: 36px;
  }

  .converter-card {
    padding: 24px;
  }

  .input-wrapper {
    flex-direction: column;
  }

  .generate-btn {
    justify-content: center;
  }
}
</style>
